import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";
import { OrderCard } from "../components/admin/OrderCard";
import { CustomersPanel } from "../components/admin/CustomersPanel";

const TABS = [["all", "Todos"], ["toship", "Por enviar"], ["paid", "Pagados"], ["transfer", "Pendientes de transferencia"], ["clientes", "Clientes"]];

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(`${window.location.origin}/pedidos`)}`;
};

const Login = ({ denied }) => (
  <main data-testid="admin-login" className="min-h-[100svh] grid place-items-center px-5 pt-24 text-center">
    <div className="max-w-md">
      <h1 className="font-serif-d font-black text-4xl">Pedidos</h1>
      <p className="text-[#C9D6C2] mt-3">{denied ? "Esta cuenta de Google no tiene acceso. Entra con la cuenta de la tienda." : "Página privada de la tienda. Entra con la cuenta de Google autorizada."}</p>
      <button type="button" onClick={login} data-testid="admin-login-button" className="mt-8 h-12 px-7 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold hover:bg-[#F2B441] transition-colors">Entrar con Google</button>
    </div>
  </main>
);

export default function AdminOrders() {
  const [user, setUser] = useState(undefined);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState("all");
  const [orders, setOrders] = useState(null);
  const [who, setWho] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const go = async () => {
      if (m) {
        window.history.replaceState(null, "", "/pedidos");
        try { setUser(await adminApi.session(decodeURIComponent(m[1]))); return; } catch (e) { setDenied(e?.response?.status === 403); setUser(null); return; }
      }
      try { setUser(await adminApi.me()); } catch (e) { setDenied(e?.response?.status === 403); setUser(null); }
    };
    go();
    return undefined;
  }, []);

  const load = useCallback(async () => {
    if (tab === "clientes") return;
    setOrders(null);
    try { const d = await adminApi.orders(tab, who?.id); setOrders(Array.isArray(d) ? d : []); } catch { setOrders([]); toast.error("No se pudieron cargar los pedidos"); }
  }, [tab, who]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const markPaid = async (id) => {
    try {
      const r = await adminApi.markPaid(id);
      if (r?.email_sent) toast.success("Pedido marcado como pagado. El cliente recibirá su confirmación.");
      else toast.warning("Pedido marcado como pagado, pero no se pudo enviar el correo al cliente. Avísale tú.");
      load();
    }
    catch { toast.error("No se pudo marcar como pagado"); }
  };
  const ship = async (id, tracking) => {
    try {
      const r = await adminApi.ship(id, tracking);
      if (r?.email_sent) toast.success("Pedido marcado como enviado. Hemos avisado al cliente por correo.");
      else toast.warning("Pedido marcado como enviado, pero no se pudo enviar el correo. Avísale tú.");
      load();
      return true;
    } catch { toast.error("No se pudo marcar como enviado"); return false; }
  };
  const logout = async () => { await adminApi.logout().catch(() => {}); setUser(null); };

  if (user === undefined) return <main className="min-h-[100svh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#8CD05A]" /></main>;
  if (!user) return <Login denied={denied} />;
  const total = (orders || []).filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);
  return (
    <main data-testid="admin-orders-page" className="max-w-[1000px] mx-auto px-5 pt-28 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-d font-black text-4xl sm:text-5xl">Pedidos</h1>
          <p className="text-sm text-[#C9D6C2] mt-2">{user.email}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} data-testid="admin-refresh" aria-label="Actualizar" className="w-11 h-11 rounded-full border border-[#F6EEDC]/20 grid place-items-center hover:border-[#F2B441]"><RefreshCw className="w-4 h-4" /></button>
          <button type="button" onClick={logout} data-testid="admin-logout" className="h-11 px-4 rounded-full border border-[#F6EEDC]/20 inline-flex items-center gap-2 text-sm hover:border-[#F2B441]"><LogOut className="w-4 h-4" /> Salir</button>
        </div>
      </div>
      <div data-lenis-prevent className="mt-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => { setTab(id); setWho(null); }} data-testid={`admin-tab-${id}`} aria-pressed={tab === id}
            className={`shrink-0 h-10 px-4 rounded-full border text-sm transition-colors ${tab === id ? "bg-[#F2B441] text-[#0E2A1C] border-[#F2B441] font-semibold" : "border-[#F6EEDC]/25 hover:border-[#F2B441]"}`}>{label}</button>
        ))}
      </div>
      {tab === "clientes" ? <CustomersPanel onShowOrders={(c) => { setWho(c); setTab("all"); }} /> : orders === null ? <Loader2 className="w-6 h-6 animate-spin text-[#8CD05A] mt-10" /> : (
        <>
          {who && (
            <p data-testid="admin-customer-filter" className="mt-5 text-sm">Pedidos de <strong>{who.name}</strong>{" "}
              <button type="button" onClick={() => setWho(null)} data-testid="admin-customer-filter-clear" className="underline underline-offset-4 text-[#F2B441]">ver todos</button></p>
          )}
          <p data-testid="admin-orders-count" className="text-sm text-[#C9D6C2] mt-5">{orders.length} pedidos{tab !== "transfer" ? ` · cobrado ${total.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}` : ""}</p>
          <div className="mt-4 space-y-3">
            {orders.length === 0 && <p data-testid="admin-orders-empty" className="text-[#C9D6C2] py-10">No hay pedidos en esta lista.</p>}
            {orders.map((o) => <OrderCard key={o.id} o={o} onMarkPaid={markPaid} onShip={ship} />)}
          </div>
        </>
      )}
    </main>
  );
}
